import { apiAdminClient } from "../_lib/apiClient";

const OBJECT_ID_PATTERN = /^[a-f0-9]{24}$/i;
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const QUOTE_FINGERPRINT_PATTERN = /^[a-f0-9]{64}$/;
const IDEMPOTENCY_KEY_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{7,254}$/;
const MAX_REASON_LENGTH = 1000;
const MAX_SERVICE_DAYS = 1095;
const COMMIT_TIMEOUT_MS = 30000;

function serviceError(message, code, data) {
  const error = new Error(message);
  error.statusCode = 400;
  error.code = code;
  if (data !== undefined) error.data = data;
  return error;
}

function normalizeBookingId(value) {
  const bookingId = typeof value === "string" ? value.trim() : "";
  if (!bookingId) {
    throw serviceError(
      "A reservation is required.",
      "reservation_amendment_booking_required",
    );
  }
  if (!OBJECT_ID_PATTERN.test(bookingId)) {
    throw serviceError(
      "The reservation identifier is invalid.",
      "invalid_amendment_booking_id",
    );
  }
  return bookingId;
}

function dateOrdinal(value, code, label) {
  if (typeof value !== "string" || !DATE_ONLY_PATTERN.test(value)) {
    throw serviceError(`${label} must use YYYY-MM-DD.`, code);
  }
  const [year, month, day] = value.split("-").map(Number);
  const parsed = new Date(0);
  parsed.setUTCHours(0, 0, 0, 0);
  parsed.setUTCFullYear(year, month - 1, day);
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    throw serviceError(`${label} must be a real calendar date.`, code);
  }
  return parsed.getTime() / (24 * 60 * 60 * 1000);
}

function normalizeReason(value) {
  const reason = typeof value === "string" ? value.trim() : "";
  if (reason.length < 10 || reason.length > MAX_REASON_LENGTH) {
    throw serviceError(
      "The amendment reason must contain between 10 and 1000 characters.",
      "invalid_amendment_reason",
    );
  }
  return reason;
}

function normalizeCommand(input = {}) {
  const source = input && typeof input === "object" ? input : {};
  const {
    bookingId,
    proposedCheckInDate,
    proposedCheckOutDate,
    reason,
    noticeWaiver,
  } = source;
  const normalizedBookingId = normalizeBookingId(bookingId);
  const checkInOrdinal = dateOrdinal(
    proposedCheckInDate,
    "invalid_proposed_reservation_check_in_date",
    "Proposed check-in date",
  );
  const checkOutOrdinal = dateOrdinal(
    proposedCheckOutDate,
    "invalid_proposed_reservation_check_out_date",
    "Proposed checkout date",
  );
  const serviceDays = checkOutOrdinal - checkInOrdinal;
  if (!Number.isSafeInteger(serviceDays) || serviceDays <= 0) {
    throw serviceError(
      "Proposed checkout must be after check-in.",
      "invalid_proposed_reservation_date_range",
    );
  }
  if (serviceDays > MAX_SERVICE_DAYS) {
    throw serviceError(
      `Select no more than ${MAX_SERVICE_DAYS} service days.`,
      "reservation_amendment_date_range_too_large",
      { maximumServiceDays: MAX_SERVICE_DAYS },
    );
  }
  if (noticeWaiver !== true) {
    throw serviceError(
      "Confirm that the reservation date change was mutually agreed.",
      "amendment_notice_waiver_required",
    );
  }

  return {
    bookingId: normalizedBookingId,
    body: {
      proposedCheckInDate,
      proposedCheckOutDate,
      reason: normalizeReason(reason),
      noticeWaiver: true,
    },
  };
}

function normalizedResponseData(
  response,
  acceptedStatusCodes,
  fallbackMessage,
) {
  const body = response?.data;
  const statusCode = Number(body?.statusCode ?? response?.status);
  const accepted =
    Number.isFinite(statusCode) && acceptedStatusCodes.includes(statusCode);
  if (!body || body.status !== true || !accepted || body.data === undefined) {
    const invalidResponse = new Error(
      body?.message || body?.userMessage || fallbackMessage,
    );
    invalidResponse.response = {
      status: Number.isFinite(statusCode) ? statusCode : response?.status,
      data: body,
    };
    throw invalidResponse;
  }
  return body.data;
}

function amendmentError(error, fallbackMessage) {
  const body = error?.response?.data;
  const data = body?.data ?? error?.data;
  const structured = new Error(
    body?.message || body?.userMessage || error?.message || fallbackMessage,
  );
  structured.statusCode =
    body?.statusCode ?? error?.response?.status ?? error?.statusCode;
  structured.code = body?.code ?? error?.code;
  structured.data = data;
  if (data?.amendment) structured.amendment = data.amendment;
  if (data?.quote) structured.quote = data.quote;
  if (!structured.quote && data?.details?.quote) {
    structured.quote = data.details.quote;
  }
  return structured;
}

async function quote(input = {}) {
  const command = normalizeCommand(input);
  try {
    const response = await apiAdminClient.post(
      `/booking/${encodeURIComponent(command.bookingId)}/amendments/quote`,
      command.body,
    );
    return normalizedResponseData(
      response,
      [200],
      "The reservation amendment quote returned an invalid response.",
    );
  } catch (error) {
    throw amendmentError(
      error,
      "The reservation amendment quote could not be loaded.",
    );
  }
}

async function commit(input = {}) {
  const source = input && typeof input === "object" ? input : {};
  const { quoteFingerprint, idempotencyKey, ...commandInput } = source;
  const command = normalizeCommand(commandInput);
  if (
    typeof quoteFingerprint !== "string" ||
    !QUOTE_FINGERPRINT_PATTERN.test(quoteFingerprint)
  ) {
    throw serviceError(
      "A valid reservation amendment quote is required.",
      "invalid_amendment_quote_fingerprint",
    );
  }
  if (
    typeof idempotencyKey !== "string" ||
    !IDEMPOTENCY_KEY_PATTERN.test(idempotencyKey)
  ) {
    throw serviceError(
      "A valid reservation amendment operation key is required.",
      "invalid_idempotency_key",
    );
  }

  try {
    const response = await apiAdminClient.post(
      `/booking/${encodeURIComponent(command.bookingId)}/amendments`,
      { ...command.body, quoteFingerprint },
      {
        headers: { "Idempotency-Key": idempotencyKey },
        config: { timeout: COMMIT_TIMEOUT_MS },
      },
    );
    return normalizedResponseData(
      response,
      [200, 202],
      "The reservation amendment request returned an invalid response.",
    );
  } catch (error) {
    throw amendmentError(
      error,
      "The reservation amendment request could not be completed.",
    );
  }
}

function createOperationKey() {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi && typeof cryptoApi.randomUUID === "function") {
    const uuid = cryptoApi.randomUUID();
    if (IDEMPOTENCY_KEY_PATTERN.test(uuid)) return uuid;
  }
  if (cryptoApi && typeof cryptoApi.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    return `reservation-amendment:${Array.from(bytes, (value) =>
      value.toString(16).padStart(2, "0"),
    ).join("")}`;
  }
  throw serviceError(
    "Secure operation-key generation is unavailable.",
    "secure_random_unavailable",
  );
}

export const reservationAmendmentService = {
  quote,
  commit,
  createOperationKey,
};
